import { AlertTriangle, CheckCircle2, Circle, XCircle } from 'lucide-react';
import type { ServiceQualitySheetView } from '@obraplan/core/application/use-cases/quality/quality-sheet-use-cases';
import {
  DAY_INSPECTION_RESULT_LABEL,
  LOCAL_STATUS_LABEL,
  localsMissingForTarget,
  type DayInspectionResult,
  type LocalStatus,
} from '@obraplan/core/domain/quality/service-quality-sheet';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableWrapper,
} from '@/components/ui/table';
import { formatNcNumbers, formatSheetDate, formatSheetQuantity } from './sheet-format';

/**
 * A ficha de verificação do serviço, na tela (Fase 24, RN-298 e RN-299).
 *
 * Componente puro: recebe a ficha lida e a desenha. Os três blocos seguem a ordem em que
 * quem confere a lê no papel — o que foi executado, a ficha, a situação de cada pavimento —,
 * e o rodapé repete as pendências, que são o que se leva da folha para a obra.
 *
 * Estado nunca só por cor (§4.4): a situação do pavimento e o resultado do dia levam
 * ícone e texto.
 */

const LOCAL_STATUS: Readonly<
  Record<LocalStatus, { tone: 'ok' | 'danger' | 'warn'; icon: typeof CheckCircle2 }>
> = {
  CONFORME: { tone: 'ok', icon: CheckCircle2 },
  NAO_CONFORME: { tone: 'danger', icon: XCircle },
  PENDENTE: { tone: 'warn', icon: Circle },
};

const DAY_RESULT_TONE: Readonly<Record<DayInspectionResult, 'ok' | 'danger' | 'warn'>> = {
  APROVADO: 'ok',
  REPROVADO: 'danger',
  PENDENTE: 'warn',
};

export function QualitySheetDocument({ view }: { view: ServiceQualitySheetView }) {
  const { sheet, service, checklist, project } = view;
  const unit =
    service.unitName === undefined
      ? undefined
      : { name: service.unitName, decimals: service.unitDecimals ?? 2 };
  const missing = localsMissingForTarget(sheet.sampling);

  if (sheet.locals.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{service.name}</CardTitle>
          <CardDescription>
            Nenhuma execução conferida deste serviço nesta obra. A ficha mostra os pavimentos onde o
            serviço foi executado e a conferência do dia foi encerrada — confira o dia em
            Conferência para ele aparecer aqui.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <Card>
        <CardHeader>
          <CardTitle>
            {checklist.code === undefined
              ? checklist.name
              : `${checklist.code} · ${checklist.name}`}
          </CardTitle>
          <CardDescription>{service.name}</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <dl className="grid gap-x-6 gap-y-3 text-sm sm:grid-cols-2 lg:grid-cols-4">
            <Campo rotulo="Obra" valor={`${project.name} (${project.code})`} />
            <Campo rotulo="Serviço" valor={service.name} />
            <Campo
              rotulo="Amostragem"
              valor={
                checklist.samplingPct === undefined ? '—' : `${String(checklist.samplingPct)}%`
              }
            />
            <Campo rotulo="Executado por" valor={sheet.executedBy.join(', ') || '—'} />
            <Campo
              rotulo="Responsável pela inspeção"
              valor={sheet.inspectedBy.join(', ') || 'Ninguém inspecionou ainda'}
            />
            <Campo rotulo="Emitida em" valor={formatSheetDate(view.today)} />
          </dl>

          {/* A amostragem: só alerta abaixo da meta (RN-299) — acima, ou sem meta, é um número. */}
          <p className="flex flex-wrap items-center gap-2 text-sm" data-testid="amostragem">
            <span>
              Inspecionados{' '}
              <strong className="tabular">
                {sheet.sampling.inspectedLocals} de {sheet.sampling.executedLocals}
              </strong>{' '}
              pavimentos executados ({String(sheet.sampling.percent)}%
              {sheet.sampling.targetPct === undefined
                ? ''
                : `, meta ${String(sheet.sampling.targetPct)}%`}
              ).
            </span>
            {sheet.sampling.belowTarget && (
              <Badge tone="warn" icon={<AlertTriangle aria-hidden="true" />}>
                {missing === 1
                  ? 'Abaixo da meta: falta inspecionar 1 pavimento'
                  : `Abaixo da meta: faltam inspecionar ${String(missing)} pavimentos`}
              </Badge>
            )}
          </p>
        </CardContent>
      </Card>

      {/* ── Bloco 1: as execuções por dia ─────────────────────────────────────────── */}
      <Card>
        <CardHeader>
          <CardTitle>Execuções por dia</CardTitle>
          <CardDescription>
            O que foi executado e conferido, por dia e pavimento, com o resultado da inspeção
            daquele dia.
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <TableWrapper>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Dia</TableHead>
                  <TableHead>Pavimento</TableHead>
                  <TableHead className="text-right">Quantidade</TableHead>
                  <TableHead>Inspeção</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sheet.executions.map((row) => (
                  <TableRow key={`${row.date}|${row.activityId}`}>
                    <TableCell className="tabular">{formatSheetDate(row.date)}</TableCell>
                    <TableCell>{row.localLabel}</TableCell>
                    <TableCell className="tabular text-right">
                      {formatSheetQuantity(row.quantity, unit)}
                    </TableCell>
                    <TableCell>
                      <Badge tone={DAY_RESULT_TONE[row.result]}>
                        {row.result === 'REPROVADO' && row.ncNumbers.length > 0
                          ? `Reprovado (NC ${formatNcNumbers(row.ncNumbers)})`
                          : DAY_INSPECTION_RESULT_LABEL[row.result]}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableWrapper>
        </CardContent>
      </Card>

      {/* ── Bloco 2: a ficha — itens × pavimentos ─────────────────────────────────── */}
      <Card>
        <CardHeader>
          <CardTitle>Ficha de verificação</CardTitle>
          <CardDescription>
            Em Aprovado, a data da verificação que aprovou o item; em Reprovado, o número da NC. A
            NC cancelada não entra.
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <TableWrapper>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead rowSpan={2}>Item de verificação</TableHead>
                  <TableHead rowSpan={2}>Tolerância</TableHead>
                  {sheet.locals.map((local) => (
                    <TableHead key={local.activityId} colSpan={2} className="text-center">
                      {local.label}
                    </TableHead>
                  ))}
                </TableRow>
                <TableRow>
                  {sheet.locals.map((local) => (
                    <FragmentoDeColunas key={local.activityId} />
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {sheet.items.map((item) => (
                  <TableRow key={item.itemId}>
                    <TableCell className="min-w-56">
                      <span className="font-medium">{item.label}</span>
                      {item.method !== undefined && (
                        <span className="block text-xs text-(--color-content-muted)">
                          {item.method}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-(--color-content-muted)">
                      {item.tolerance ?? 'NA'}
                    </TableCell>
                    {sheet.locals.map((local) => {
                      const cell = item.cells[local.activityId];
                      return (
                        <CelulasDoItem
                          key={local.activityId}
                          label={`${item.label} — ${local.label}`}
                          approvedOn={cell?.approvedOn}
                          ncNumbers={cell?.ncNumbers ?? []}
                        />
                      );
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableWrapper>
        </CardContent>
      </Card>

      {/* ── Bloco 3: a situação por pavimento ─────────────────────────────────────── */}
      <Card>
        <CardHeader>
          <CardTitle>Situação por pavimento</CardTitle>
          <CardDescription>
            Conforme: todos os itens aprovados. Não conforme: há NC que segue de pé. Pendente:
            executado, mas falta inspecionar algum item.
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <TableWrapper>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Pavimento</TableHead>
                  <TableHead>Situação</TableHead>
                  <TableHead>NCs</TableHead>
                  <TableHead>Falta inspecionar</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sheet.situations.map((situation) => {
                  const local = sheet.locals.find(
                    (item) => item.activityId === situation.activityId,
                  );
                  const { tone, icon: Icon } = LOCAL_STATUS[situation.status];
                  return (
                    <TableRow key={situation.activityId}>
                      <TableCell className="font-medium">{local?.label}</TableCell>
                      <TableCell>
                        <Badge tone={tone} icon={<Icon aria-hidden="true" />}>
                          {LOCAL_STATUS_LABEL[situation.status]}
                        </Badge>
                      </TableCell>
                      <TableCell className="tabular">
                        {situation.ncNumbers.length === 0
                          ? '—'
                          : formatNcNumbers(situation.ncNumbers)}
                      </TableCell>
                      <TableCell className="text-xs text-(--color-content-muted)">
                        {situation.missingItems.length === 0
                          ? '—'
                          : situation.missingItems.join('; ')}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableWrapper>
        </CardContent>
      </Card>

      <section
        aria-label="Considerações"
        className="rounded-md border border-(--color-border-subtle) p-4 text-sm"
      >
        <h2 className="font-semibold">Considerações (locais pendentes de inspeção)</h2>
        <p className="mt-1 text-(--color-content-muted)">
          {sheet.pending.length === 0
            ? 'Nenhum pavimento pendente de inspeção.'
            : sheet.pending.map((local) => local.label).join(', ')}
        </p>
      </section>
    </div>
  );
}

function Campo({ rotulo, valor }: { rotulo: string; valor: string }) {
  return (
    <div>
      <dt className="text-xs text-(--color-content-muted)">{rotulo}</dt>
      <dd className="font-medium">{valor}</dd>
    </div>
  );
}

/** A segunda linha do cabeçalho: sob cada pavimento, Aprovado e Reprovado. */
function FragmentoDeColunas() {
  return (
    <>
      <TableHead className="text-center text-xs font-normal">Aprovado</TableHead>
      <TableHead className="text-center text-xs font-normal">Reprovado</TableHead>
    </>
  );
}

/**
 * As duas células do item num pavimento: a data em que se aprovou e as NCs que seguem de pé.
 *
 * Vazias dizem "ainda não" — e o texto lido por leitor de tela diz qual item e qual
 * pavimento, porque a célula sozinha, fora da grade, não diz nada.
 */
function CelulasDoItem({
  label,
  approvedOn,
  ncNumbers,
}: {
  label: string;
  approvedOn: string | undefined;
  ncNumbers: readonly number[];
}) {
  return (
    <>
      <TableCell className="tabular text-center text-xs">
        {approvedOn === undefined ? (
          <span aria-label={`${label}: sem aprovação`} />
        ) : (
          formatSheetDate(approvedOn)
        )}
      </TableCell>
      <TableCell className="tabular text-center text-xs">
        {ncNumbers.length === 0 ? (
          <span aria-label={`${label}: sem reprovação`} />
        ) : (
          <span className="font-medium text-(--color-state-danger)">
            NC {ncNumbers.map(String).join(', ')}
          </span>
        )}
      </TableCell>
    </>
  );
}
